import React from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

/**
 * LockedField
 *
 * Wraps any form field to visually indicate it is inherited from a parent agent
 * and cannot be edited on the child agent directly.
 *
 * Features:
 * - LockClosedIcon badge in top-right corner
 * - group-hover tooltip explaining why it is locked
 * - Overlay div that blocks pointer events so the inner control stays mounted
 *   but is non-interactive
 * - aria-readonly / aria-disabled on the wrapper for a11y
 */
function LockedField({ children, label }) {
  const { t } = useTranslation();
  const tooltip = t('agentEditor.lockedField', 'Inherited from parent — edit parent to change');

  return (
    <div
      className="relative group"
      aria-readonly="true"
      aria-disabled="true"
    >
      {/* Lock badge */}
      <div className="absolute top-0 right-0 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-bl-md rounded-tr-xl bg-hover border border-border text-muted text-xs select-none max-w-[40%] flex-shrink-0">
        <LockClosedIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline truncate">{t('agentEditor.locked', 'Locked')}</span>
      </div>

      {/* Tooltip on hover */}
      <div
        className="absolute -bottom-8 left-0 z-20 hidden group-hover:block whitespace-nowrap rounded-lg bg-foreground text-background text-xs px-2 py-1 shadow-lg pointer-events-none"
        role="tooltip"
      >
        {tooltip}
      </div>

      {/* Content wrapper — blocks interaction via pointer-events-none overlay.
          pt-6 reserves space at the top so the absolute Lock badge does not
          overlap the first row's content (e.g. titles/toggles in card rows). */}
      <div className="cursor-not-allowed opacity-70 pt-8">
        <div className="pointer-events-none select-none">
          {children}
        </div>
      </div>
    </div>
  );
}

export default LockedField;
